import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Company } from '../../../services/company.service';
import { StripeService } from '../../../services/stripe.service';
import { CompanyService } from '../../../services/company.service';
import { firstValueFrom } from 'rxjs';
import { TokenStorageService } from '../../../services/token-storage.service';
import { LoggerService } from '../../../services/logger.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBanknote,
  lucideChevronRight,
  lucideCircleCheck,
  lucideClock,
  lucideEyeOff,
  lucideLock,
  lucideShieldCheck,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';

interface StepChange {
  step: string;
}

@Component({
  selector: 'app-financial-settings',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideBanknote,
      lucideChevronRight,
      lucideCircleCheck,
      lucideClock,
      lucideEyeOff,
      lucideLock,
      lucideShieldCheck,
      lucideTriangleAlert,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './financial-settings.component.html',
})
export class FinancialSettingsComponent implements OnInit, OnDestroy {
  loading = false;
  showLoadingSpinner = true;
  error: string | null = null;
  isStripeOnboardingSuccess = false;
  /** Everything is submitted, Stripe is still verifying: not an error, just wait. */
  stripeReviewPending = false;
  /** The status check on opening the page runs once, not on every company update. */
  private statusChecked = false;
  company: Company | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private stripeService: StripeService,
    private companyService: CompanyService,
    private router: Router,
    private tokenStorage: TokenStorageService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company: Company) => {
      this.company = company;
      // Network members are paid out by the network and have no Stripe account.
      if (company?.is_member) {
        this.showLoadingSpinner = false;
        return;
      }

      // The connected account can be replaced server-side (changing a company's
      // country recreates it), which leaves the cached ID pointing at a deleted
      // account and every Stripe call failing to authenticate.
      if (
        company?.stripe_account_id &&
        company.stripe_account_id !== this.tokenStorage.getCompanyStripeAccountId()
      ) {
        this.logger.log('🏦 [FinancialSettings] Stripe account changed - refreshing cached ID');
        this.tokenStorage.setCompanyStripeAccountId(company.stripe_account_id);
      }

      // Check if Stripe can be initialized
      if (!this.stripeService.canInitializeStripe()) {
        this.error = this.stripeService.getStripeInitializationError();
        this.loading = false;
        return;
      }

      if (this.company?.is_stripe_onboarding_success) {
        this.isStripeOnboardingSuccess = true;
        this.manageStripeAccount();
      } else {
        this.checkStripeStatusOnce();
        this.onboardStripe();
      }
    });
  }

  /**
   * Stripe may finish verifying after the user left the onboarding. Opening this page
   * again then completes the setup without another pass through the form.
   */
  private async checkStripeStatusOnce(): Promise<void> {
    if (this.statusChecked) return;
    this.statusChecked = true;
    const accountId = this.tokenStorage.getCompanyStripeAccountId();
    if (!accountId) return;
    try {
      const status = await firstValueFrom(this.stripeService.getAccountStatus(accountId));
      if (status?.details_submitted && status.charges_enabled && status.payouts_enabled) {
        this.markOnboardingSucceeded(false);
      } else if (status?.details_submitted) {
        this.stripeReviewPending = true;
      }
    } catch (err) {
      this.logger.error('Failed to check Stripe account status:', err);
    }
  }

  /** Stripe can take payments: store it, refresh the company, optionally go home. */
  private markOnboardingSucceeded(goHome: boolean): void {
    this.stripeReviewPending = false;
    const companyId = this.tokenStorage.getPublicCompanyId() || '';
    const done = () => {
      if (goHome) this.router.navigate(['/'], { queryParams: { onboarding_success: 'true' } });
    };
    this.companyService
      .updateCompany({ public_id: companyId, is_stripe_onboarding_success: true })
      .subscribe(() => {
        if (!companyId) return done();
        // Reload company data to update the observable
        this.companyService.getCompany(companyId).subscribe((updatedCompany) => {
          this.companyService.updateCompanyState(updatedCompany);
          done();
        });
      });
  }

  private async onboardStripe() {
    try {
      this.loading = true;
      this.error = null;

      const accountOnboarding =
        await this.stripeService.createStripeComponent('account-onboarding');

      const container = document.getElementById('stripe-connect-mount');
      if (container && !container.hasChildNodes()) {
        // Set up loading handlers
        if (typeof accountOnboarding.setOnLoaderStart === 'function') {
          accountOnboarding.setOnLoaderStart((event: { elementTagName: string }) => {
            const loader = document.getElementById('stripe-mount-loader');
            if (loader) loader.style.display = 'none';
            // this.logger.log(`Account onboarding (${event.elementTagName}) is visible to users`);
          });
        }

        // Mount component
        container.appendChild(accountOnboarding);

        // Set up event handler
        if (accountOnboarding.setOnEvent) {
          accountOnboarding.setOnEvent((event: any) => {
            // this.logger.log('Account onboarding event:', event);
          });
        }
      } else {
        throw new Error('Could not find stripe-connect-mount element or it already has content');
      }

      // Set up other event handlers
      accountOnboarding.setOnExit(async () => {
        // this.logger.log('User exited the onboarding flow');
        this.loading = true;

        try {
          const accountId = this.tokenStorage.getCompanyStripeAccountId();
          if (!accountId) {
            throw new Error('No Stripe account ID found');
          }

          const accountStatus = await firstValueFrom(
            this.stripeService.getAccountStatus(accountId),
          );
          if (!accountStatus) {
            throw new Error('Failed to get account status');
          }

          if (
            accountStatus.details_submitted &&
            accountStatus.charges_enabled &&
            accountStatus.payouts_enabled
          ) {
            this.markOnboardingSucceeded(true);
          } else if (accountStatus.details_submitted) {
            // Right after the last step Stripe usually still verifies the account, so
            // payments and payouts are not enabled yet. Nothing is missing on our side.
            this.error = null;
            this.stripeReviewPending = true;
          } else {
            this.stripeReviewPending = false;
            this.error =
              'Bei Stripe fehlen noch Angaben. Bitte schließe die Einrichtung unten ab, damit deine Kunden bezahlen können.';
          }
        } catch (err: any) {
          this.logger.error('Failed to check account status:', err);
          if (err.status === 401) {
            this.error = 'Authentifizierungsfehler. Bitte melden Sie sich erneut an.';
          } else {
            this.error = 'Fehler beim Überprüfen des Kontostatus. Bitte versuchen Sie es erneut.';
          }
        } finally {
          this.loading = false;
        }
      });

      accountOnboarding.setOnStepChange((stepChange: StepChange) => {
        // this.logger.log(`User entered: ${stepChange.step}`);
      });

      accountOnboarding.setFullTermsOfServiceUrl('https://www.schenkwerk.com/agbs');
      accountOnboarding.setPrivacyPolicyUrl('https://www.schenkwerk.com/datenschutz');
    } catch (err: any) {
      this.error = err.message || 'An error occurred during Stripe setup';
      this.logger.error('Stripe setup error:', err);
    } finally {
      this.loading = false;
    }
  }

  private async manageStripeAccount() {
    try {
      const accountManagement =
        await this.stripeService.createStripeComponent('account-management');

      // this.logger.log('Manage Stripe Account');

      const container = document.getElementById('stripe-account-management-mount');
      // this.logger.log('Container:', container);
      if (container && !container.hasChildNodes()) {
        // Set up loading handlers
        if (typeof accountManagement.setOnLoaderStart === 'function') {
          accountManagement.setOnLoaderStart((event: { elementTagName: string }) => {
            const loader = document.getElementById('stripe-mount-loader');
            if (loader) loader.style.display = 'none';
            // this.logger.log(`Account management (${event.elementTagName}) is visible to users`);
          });
        }

        // Mount component
        container.appendChild(accountManagement);

        // Set up event handler
        if (accountManagement.setOnEvent) {
          accountManagement.setOnEvent((event: any) => {
            // this.logger.log('Account management event:', event);
          });
        }
      } else {
        throw new Error(
          'Could not find stripe-account-management-mount element or it already has content',
        );
      }
    } catch (err: any) {
      this.error = err.message || 'An error occurred during Stripe account management';
      this.logger.error('Stripe account management error:', err);
    }
  }

  goToOnboarding() {
    this.router.navigate(['/onboarding']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
